import { Box, Flex, Table } from "@mantine/core";
import { Button, EmptyMessage, Loader, Text } from "components";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditRequestAttachmentResponse, CreditResponse } from "types";
import CreditAttachmentDocumentationItem from "./item";
import AttachmentUploadModal from "./upload";
import { IRootState } from "config/redux";
import { useSelector } from "react-redux";
import { AppUtils } from "utils";
import { ServiceConstants } from "constant";
import { IconPlus } from "@tabler/icons-react";

const { REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID, REQUEST_PENDING } = ServiceConstants;

interface CreditDeliveredDocumentationProps {
  credit: CreditResponse;
  attachments?: CreditRequestAttachmentResponse[];
  loading: boolean;
  t: TFunction;
  isNewAllowed: boolean;
}

const CreditDeliveredDocumentation = ({ credit, loading, attachments, t, isNewAllowed }: CreditDeliveredDocumentationProps) => {

  const uploadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID]);

  const [tempAttachments, setTempAttachments] = useState<CreditRequestAttachmentResponse[] | undefined>(undefined);
  const [newOpened, setNewOpened] = useState<boolean>(false);

  useEffect(() => {
    setTempAttachments(attachments);
  }, [attachments])

  useEffect(() => {
    AppUtils.resolveServiceObject(uploadServiceObject, REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID, (val) => {
      let oldAttachments = tempAttachments ? [...tempAttachments] : [];
      oldAttachments = [...oldAttachments, ...val];
      setTempAttachments(oldAttachments);
      setNewOpened(false);
    });
  }, [uploadServiceObject]);

  const updateAttachment = useCallback((attachment: CreditRequestAttachmentResponse) => {
    let tempIndex = tempAttachments!.findIndex(el => el.id === attachment.id);
    if (tempIndex !== -1) {
      const temp = tempAttachments ? [...tempAttachments] : [];
      temp[tempIndex] = attachment;
      setTempAttachments(temp);
    }
  }, [tempAttachments]);

  const removeAttachment = useCallback((id: string) => {
    setTempAttachments(tempAttachments?.filter(el => el.id !== id));
  }, [tempAttachments]);

  const isLoading = useMemo(() => uploadServiceObject && uploadServiceObject.status === REQUEST_PENDING, [uploadServiceObject]);

  const rows = useMemo(() => {
    if (!tempAttachments) {
      return <></>;
    }
    return tempAttachments.map(el => <CreditAttachmentDocumentationItem credit={credit} item={el} updateAttachment={updateAttachment} removeAttachment={removeAttachment} t={t} />)
  }, [tempAttachments])

  return (
    <>
      <Flex align={'center'} justify={'space-between'} mb={'sm'}>
        <Text ml={'md'}>{t('credit.documentation.delivered.title')}</Text>
        <Button
          disabled={!isNewAllowed || loading || isLoading}
          loading={isLoading}
          onClick={() => setNewOpened(true)}
          size='compact-xs'>
            <IconPlus />
        </Button>
        {isNewAllowed &&
          <AttachmentUploadModal
            opened={newOpened}
            onClose={() => setNewOpened(false)}
            creditId={credit.id}
            t={t}
          />
        }
      </Flex>
      <Box bg={'var(--mantine-color-gray-1)'} style={{ borderRadius: 8 }} p={'md'}>
        <Box pos={'relative'}>
          <Loader overlay={true} visible={!attachments || loading} />
          {tempAttachments &&
            tempAttachments.length === 0 ?
              <EmptyMessage message={t('credit.documentation.delivered.list.empty')} />
            :
            <Table withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th colSpan={2}>{t('credit.documentation.delivered.table.documents')}</Table.Th>
                  <Table.Th>{t('credit.documentation.delivered.table.delivered.by')}</Table.Th>
                  <Table.Th>{t('credit.documentation.delivered.table.delivered.at')}</Table.Th>
                  <Table.Th>&nbsp;</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows}
              </Table.Tbody>
            </Table>
          }
        </Box>
      </Box>
    </>
  )
}

export default CreditDeliveredDocumentation;